import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { workflowDbApi } from "./workflow-db.api";
import { normalizeWorkflowDbSchema, normalizeWorkflowDbTableData } from "./workflow-db.schema";
import { useWorkflowDbStore } from "./workflow-db.store";
import type {
	UpdateRecordRequest,
	UpdateWorkflowDbSchemaRequest,
} from "./workflow-db.types";

export const workflowDbKeys = {
	all: ["workflow-db"] as const,
	data: (workflowId: string) => [...workflowDbKeys.all, "data", workflowId] as const,
	schema: (workflowId: string) => [...workflowDbKeys.all, "schema", workflowId] as const,
};

export const useGetWorkflowDbData = (
	workflowId: string,
	options?: { polling?: boolean },
) => {
	const setData = useWorkflowDbStore((state) => state.setData);
	const schema = useWorkflowDbStore((state) => state.schema);

	const query = useQuery({
		queryKey: workflowDbKeys.data(workflowId),
		queryFn: () => workflowDbApi.getData(workflowId),
		enabled: Boolean(workflowId),
		refetchInterval: options?.polling ? 5000 : false,
	});

	useEffect(() => {
		if (!query.data || schema.properties.length === 0) return;

		const tableNames = schema.properties
			.filter((field) => field.type === "array" && Array.isArray(field.arrayItemProperties))
			.map((field) => field.key);
		setData(normalizeWorkflowDbTableData(query.data.data, tableNames));
	}, [query.data, schema, setData]);

	return query;
};

export const useGetWorkflowDbSchema = (workflowId: string) => {
	const setSchema = useWorkflowDbStore((state) => state.setSchema);

	const query = useQuery({
		queryKey: workflowDbKeys.schema(workflowId),
		queryFn: () => workflowDbApi.getSchema(workflowId),
		enabled: Boolean(workflowId),
	});

	useEffect(() => {
		if (query.data) setSchema(normalizeWorkflowDbSchema(query.data.schema, query.data.enums));
	}, [query.data, setSchema]);

	return query;
};

export const useUpdateWorkflowDbSchema = () => {
	const queryClient = useQueryClient();
	const setSchema = useWorkflowDbStore((state) => state.setSchema);

	return useMutation({
		mutationFn: ({
			workflowId,
			request,
		}: {
			workflowId: string;
			request: UpdateWorkflowDbSchemaRequest;
		}) => workflowDbApi.updateSchema(workflowId, request),
		onSuccess: (data, variables) => {
			const nextSchema = normalizeWorkflowDbSchema(data.schema, data.enums);
			setSchema(nextSchema);
			queryClient.setQueryData(workflowDbKeys.schema(variables.workflowId), data);
		},
	});
};

export const useWriteRecord = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			workflowId,
			table,
			data,
		}: {
			workflowId: string;
			table: string;
			data: Record<string, unknown>;
		}) => workflowDbApi.writeRecord(workflowId, table, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: workflowDbKeys.data(variables.workflowId),
			});
		},
	});
};

export const useDeleteRecord = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			workflowId,
			table,
			ref,
		}: {
			workflowId: string;
			table: string;
			ref: string;
		}) => workflowDbApi.deleteRecord(workflowId, table, ref),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: workflowDbKeys.data(variables.workflowId),
			});
		},
	});
};

export const useUpdateRecord = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			workflowId,
			request,
		}: {
			workflowId: string;
			request: UpdateRecordRequest;
		}) => workflowDbApi.updateRecord(workflowId, request),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: workflowDbKeys.data(variables.workflowId),
			});
		},
	});
};

export const useReadRecord = () => {
	return useMutation({
		mutationFn: ({
			workflowId,
			ref,
			select,
		}: {
			workflowId: string;
			ref: string;
			select?: string[];
		}) => workflowDbApi.readRecord(workflowId, ref, select),
	});
};
